import { redirect } from '@tanstack/react-router';
import {
  LINK_NOTIFICATION_PARAM,
  LINK_SOURCE_PARAM,
  LINK_SOURCES,
  type LinkClickRequest,
  type LinkSource,
} from '@wundo/shared/linkClick.ts';
import { apiFetch } from '@wundo/client/api.ts';
import { reportError } from './reportError.ts';

/**
 * Report an arrival through a link Wundo sent, then drop its tracking params
 * from the address. Called by the guards once there is a session, so the params
 * ride along through a sign-in and only a signed-in visit counts.
 */
export function takeLinkClick(href: string): void {
  const url = new URL(href, window.location.origin);
  const src = url.searchParams.get(LINK_SOURCE_PARAM);
  if (!isLinkSource(src)) return;

  const n = Number(url.searchParams.get(LINK_NOTIFICATION_PARAM));
  url.searchParams.delete(LINK_SOURCE_PARAM);
  url.searchParams.delete(LINK_NOTIFICATION_PARAM);

  void reportLinkClick({
    src,
    ...(Number.isSafeInteger(n) && n > 0 ? { n } : {}),
    path: url.pathname,
  });

  throw redirect({ href: `${url.pathname}${url.search}${url.hash}`, replace: true });
}

function isLinkSource(value: string | null): value is LinkSource {
  return LINK_SOURCES.some((source) => source === value);
}

/* A lost count is not worth bothering the visitor with. */
async function reportLinkClick(click: LinkClickRequest): Promise<void> {
  try {
    await apiFetch('/link-clicks', {
      method: 'POST',
      body: JSON.stringify(click),
    });
  } catch (caught) {
    reportError(caught, { action: 'reportLinkClick', src: click.src });
  }
}
